import React from "react";
import { Edit, Trash2, Info, Loader } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { formatNumberWithSeparator } from "../utils/numberFormat";
import { HasPermission } from "../hooks/usePermission";

export default function CustomerTable({
  customers,
  searchTerm,
  sortBy,
  sortOrder,
  loading,
  error,
  totalEntries,
  currentPage,
  maxPages,
  startEntry,
  endEntry,
  itemsPerPage,
  onSearch,
  onSort,
  onEdit,
  onDelete,
  onInfo,
  onPageChange,
  onItemsPerPageChange,
}) {
  const getStatusColor = (status) => {
    return status === "ACTIVE"
      ? "bg-emerald-50 ring-1 ring-emerald-200 text-emerald-700"
      : "bg-rose-50 ring-1 ring-rose-200 text-rose-700";
  };

  const getStatusLabel = (status) => {
    return status === "ACTIVE" ? "Aktif" : "Non-Aktif";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // SortIcon removed as per user request

  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm overflow-hidden">
      {/* Loading State */}
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-slate-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="px-6 py-4 bg-rose-50 border-b border-rose-200 text-rose-700">
          Error: {error}
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">
                    Aksi
                  </th>
                  <th
                    onClick={() => onSort("status")}
                    className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors">
                    Status
                  </th>
                  <th
                    onClick={() => onSort("name")}
                    className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors">
                    Nama Pelanggan
                  </th>
                  <th
                    onClick={() => onSort("phone")}
                    className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors">
                    No. Handphone
                  </th>
                  <th
                    onClick={() => onSort("total_transactions")}
                    className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors">
                    Transaksi
                  </th>
                  <th
                    onClick={() => onSort("total_spending")}
                    className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors">
                    Total Belanja
                  </th>
                  <th
                    onClick={() => onSort("last_transaction")}
                    className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors">
                    Transaksi Terakhir
                  </th>
                  <th
                    onClick={() => onSort("average_transaction")}
                    className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors">
                    Average Transaksi
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-12 text-center text-slate-400 text-sm">
                      Tidak ada data pelanggan
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                      {/* Action Buttons */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <HasPermission permission="customer.update">
                            <button
                              onClick={() => onEdit(customer.id)}
                              className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                              title="Edit">
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          </HasPermission>
                          <HasPermission permission="customer.delete">
                            <button
                              onClick={() => onDelete(customer.id)}
                              className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                              title="Hapus">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </HasPermission>
                          <button
                            onClick={() => onInfo(customer.id)}
                            className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                            title="Detail">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getStatusColor(
                            customer.status,
                          )}`}>
                          {getStatusLabel(customer.status)}
                        </span>
                      </td>

                      {/* Name */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-medium">
                        {customer.name}
                      </td>

                      {/* Phone */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {customer.phone}
                      </td>

                      {/* Transactions Count */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {customer.total_transactions}
                      </td>

                      {/* Total Spending */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-semibold">
                        Rp {formatNumberWithSeparator(customer.total_spending)}
                      </td>

                      {/* Last Transaction */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {formatDate(customer.last_transaction)}
                      </td>

                      {/* Average Transaksi */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-medium">
                        Rp {formatNumberWithSeparator(customer.average_transaction)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">
                Showing{" "}
                <span className="font-medium text-slate-700">{startEntry}</span>{" "}
                to{" "}
                <span className="font-medium text-slate-700">{endEntry}</span>{" "}
                of{" "}
                <span className="font-medium text-slate-700">
                  {totalEntries}
                </span>{" "}
                entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(value) => onItemsPerPageChange(value)}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ‹‹
              </button>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                // Simple logic to show current page and surrounding
                let p = currentPage;
                if (currentPage === 1) p = 1;
                else if (currentPage === maxPages) p = maxPages - 2;
                else p = currentPage - 1;

                // Adjust if maxPages is small
                if (maxPages < 3) p = 1;

                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  onPageChange(Math.min(maxPages, currentPage + 1))
                }
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ›
              </button>
              <button
                onClick={() => onPageChange(maxPages)}
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ››
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
